import { type, type Type } from 'arktype';
import type { ShiftOverrideApprovalInfo } from '@zag/core/models';
import { dayKeySchema } from './day-key.model';
import { absenceRangeError, type AbsencePortion } from './absence-payload.model';
import type { ApprovalState } from './approval-state.model';
import { absencePortionSchema, portionCouplingError } from './timetracking-absence.model';
import { ampelThresholdsAreConsistent } from './balance-type-definition.model';
import type { BlockedWindowRequestResult, BlockedWindowSeverity, BlockedWindowSource } from './blocked-window.model';
import type { FindingJustification, LegalReference } from './finding.model';
import type { OverviewFlexitimeBalance } from './overview.model';
import type { ReviewQueueSeverity, VerdictParams } from './review-queue.model';
import { narrowError } from './schema-narrow';
import { utcInstantSchema } from './utc-instant.model';

/** How a booking record was created (maps to `booking_records.entry_method`). */
export const bookingEntryMethods = ['live_punch', 'manual_employee', 'manual_supervisor'] as const;
export type BookingEntryMethod = (typeof bookingEntryMethods)[number];

/** Review dimension of a booking in the plan→confirm flow (`rejected` per spec §3d-12). */
export const myWeekReviewStatuses = ['proposed', 'confirmed', 'auto_approved', 'needs_review', 'approved', 'rejected'] as const;
export type MyWeekReviewStatus = (typeof myWeekReviewStatuses)[number];

export const deviationReasons = ['overtime', 'handover', 'extended_shift'] as const;
export type DeviationReason = (typeof deviationReasons)[number];

// Empty body confirms unchanged; optional time corrections require ISO-8601 UTC instants ending in Z.
export const confirmBookingSchema = type({
  'startTime?': utcInstantSchema,
  'endTime?': utcInstantSchema,
  'breakMinutes?': 'number.integer >= 0',
  'deviationReason?': "'overtime' | 'handover' | 'extended_shift'",
});

export type ConfirmBookingInput = typeof confirmBookingSchema.t;

/**
 * Body of `POST /time-tracking/my-week/confirm`. Confirms every `proposed`
 * booking of the week without deviation; `excludeDates` carries days the
 * user edited locally (they need an individual confirm with a reason).
 */
export const confirmWeekSchema = type({
  week: 'string',
  'excludeDates?': dayKeySchema.array(),
});

export type ConfirmWeekInput = typeof confirmWeekSchema.t;

/** Inputs of `POST planning/shift-swaps/mark` for a plan booking whose shift unit the employee may override. */
export interface MyWeekEmployeeOverride {
  planInstanceId: string;
  /** Bare shift unit id (not the `person:date:unit` composite of `MyWeekBooking.shiftUnitId`). */
  shiftUnitId: string;
  defaultBreakMinutes: number;
}

/** One booking entry inside a `MyWeekDay`. */
export interface MyWeekBooking {
  id: string;
  bookingDate: string;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
  reviewStatus: MyWeekReviewStatus;
  plannedStartTime: string | null;
  plannedEndTime: string | null;
  plannedBreakMinutes: number;
  /**
   * Display name of the planned shift (resolved to the request language) —
   * lets the week render plan blocks like the month planning does. Null for
   * bookings without a resolvable shift reference.
   */
  shiftName: string | null;
  /**
   * The published-shift unit this booking represents when known — carries the
   * `person:date:unit` line id used to derive stable per-unit identity across
   * republishes. Null for non-shift bookings.
   */
  shiftUnitId: string | null;
  /** True for a shift unit credited to flexitime as worked time; it renders and counts like a stamp. */
  creditedAsWorked?: boolean;
  /** |actual net minutes − planned net minutes| */
  deviationMinutes: number;
  deviationReason: DeviationReason | null;
  confirmedAt: string | null;
  /** Reviewer comment of a rejection (`metadata.rejection.comment`, spec §3d-12); null unless rejected. */
  rejectionComment: string | null;
  /**
   * True when the employee has disabled this shift unit (`metadata.exclusion`
   * present). The row stays visible for restore, but the disabled work no
   * longer contributes to actual totals and accounting.
   */
  excluded: boolean;
  /** Whether the booking was worked from home. */
  homeOffice: boolean;
  /** Free-text remark for the booking. */
  note: string | null;
  /** How the booking record was created; null for plan proposals. */
  entryMethod: BookingEntryMethod | null;
  blockedWindowRequest?: BlockedWindowRequestResult;
  approvalState?: ApprovalState | null;
  /** Latest pending/decided shift time-override request for this plan unit; null/absent when none. */
  shiftOverrideApproval?: ShiftOverrideApprovalInfo | null;
  /** Set only when the planned shift unit is employee-overridable; the only source for the UI mark call. */
  employeeOverride?: MyWeekEmployeeOverride | null;
}

/**
 * Previous-week suggestion for a plan-less day (story 31-6): the same
 * weekday's confirmed booking of the week before, times shifted by +7 days.
 */
export interface MyWeekSuggestion {
  sourceBookingId: string;
  /** Date (`YYYY-MM-DD`) of the source booking (previous week). */
  sourceDate: string;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
}

/** Source category of an actual-time entry in the "Your Week" view (F16a). */
export const myWeekEntrySources = ['plan', 'stamp', 'manual'] as const;
export type MyWeekEntrySource = (typeof myWeekEntrySources)[number];

/**
 * One actual-time booking of a day (story 35-1, F16a): EVERY active
 * ledger booking that represents worked time — plan proposals, clock stamps,
 * and manual entries. Synthetic rows (expected workdays) and absence markers
 * are excluded. `endTime: null` + `open: true` = the currently open stamp.
 */
export interface MyWeekEntry {
  id: string;
  source: MyWeekEntrySource;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number;
  /** Net worked minutes (gross span minus break); 0 while the stamp is open. */
  minutes: number;
  reviewStatus: MyWeekReviewStatus;
  /** `true` for the open clock stamp (no endTime yet). */
  open: boolean;
  /**
   * `true` for a closed zero-minute clock stamp ("Fehlstempel?", spec §3d-20):
   * clock-in and clock-out within the same minute — offered for discarding.
   */
  misstamp: boolean;
  /**
   * `true` when the employee has requested this entry to be deleted
   * (`metadata.deleteRequest`) and a reviewer has not yet approved it. The
   * entry is still active in the ledger; the UI highlights it as pending.
   */
  pendingDelete: boolean;
  /** Whether the entry was worked from home. */
  homeOffice: boolean;
  /** Free-text remark for the booking (max 500 chars). */
  note: string | null;
  /** How the booking record was created; null for plan proposals / synthetic rows. */
  entryMethod: BookingEntryMethod | null;
  /** Reviewer comment of a rejection (`metadata.rejection.comment`, spec §3d-12); null unless rejected. */
  rejectionComment: string | null;
  /** Shift booking id this manual entry was captured from; null for free manual entries. Matches MyWeekBooking.id, NOT shiftUnitId. */
  capturedFromShiftUnitId: string | null;
  /** Deviation reason selected by the employee for this entry (only relevant for captured shift-unit entries). */
  deviationReason: DeviationReason | null;
  approvalState?: ApprovalState | null;
}

/** The caller's open clock stamp, surfaced in the week view (story 35-1). */
export interface MyWeekOpenStamp {
  // ISO-8601 UTC instant ending in Z.
  since: string;
}

/**
 * Read-only absence row of a day (spec §3e-26, F19b): the absence marker from
 * the booking ledger (`absence.<type>` source), shown so the employee sees WHY
 * a day has no target. Never editable in "Deine Woche" — absence self-service
 * stays M5 by design.
 */
export interface MyWeekAbsence {
  id: string;
  /** Absence type key — the `absence.<type>` sourceType suffix (e.g. `vacation`). */
  absenceType: string;
  /** Credited hours of the day from the absence marker; null when unknown. */
  amountHours: number | null;
  /** Credited days of the day (1 = full day); null when unknown. */
  amountDays: number | null;
  /** True while the absence is an undecided approval request — rendered with the pending treatment. */
  isPending?: boolean;
  requestId?: string;
  requestedByUserId?: string;
  /** Route to the approval request; only set for pending entries. */
  deepLink?: string;
  /** Stored slot portion; absent for whole-day and legacy rows (rendered whole-day). */
  portion?: AbsencePortion;
  /** Day-local `HH:mm` range start; `range` portions only, absent otherwise. */
  startTime?: string;
  /** Day-local `HH:mm` range end; `range` portions only, absent otherwise. */
  endTime?: string;
  /** False for partial absences; absent for whole-day rows (rendered whole-day). */
  isAllDay?: boolean;
  /** Fraction of the day (0..1); `fraction` portions only, absent otherwise. */
  fractionValue?: number | null;
  /** Credited minutes; `hours` portions only, absent otherwise. */
  durationMinutes?: number | null;
  /** Runtime pending-update/remove marker stamped on committed rows (never persisted, never imported from api). */
  pendingKind?: 'update' | 'remove';
}

export function isPurePendingRequest(absence: MyWeekAbsence): boolean {
  return absence.isPending === true && absence.pendingKind == null;
}

export interface FlexitimeThresholds {
  amberMin: number;
  greenMin: number;
  greenMax: number;
  amberMax: number;
}

export const flexitimeThresholdsSchema: Type<FlexitimeThresholds> = type({
  amberMin: 'number',
  greenMin: 'number',
  greenMax: 'number',
  amberMax: 'number',
})
  .narrow(
    (thresholds, ctx) =>
      ampelThresholdsAreConsistent({
        thresholdAmberMin: thresholds.amberMin,
        thresholdGreenMin: thresholds.greenMin,
        thresholdGreenMax: thresholds.greenMax,
        thresholdAmberMax: thresholds.amberMax,
      }) || ctx.mustBe('thresholds with amberMin <= greenMin <= 0 <= greenMax <= amberMax')
  )
  .onUndeclaredKey('reject');

/**
 * Saldo widget of "Deine Woche": one balance type flagged `showInWeek` in the
 * balance config (`balance_type_definitions.show_in_week`). Which balances
 * appear here is an admin decision, not a hardcoded set. `annualBudget` stays
 * the static default/entitlement; the vacation card is driven by `available`.
 */
export interface MyWeekBalance {
  /** Balance type key (`flexitime`, `vacation`, …). */
  balanceType: string;
  /** Display label resolved to the request language. */
  label: string;
  icon: string;
  color: string;
  /** `'hours'` or `'days'` — the unit of `value` and `annualBudget`. */
  unit: string;
  /** Balance as of the server today (opening + postings ≤ today), in `unit`. */
  value: number;
  /** Annual budget/entitlement in `unit`; null when not configured. */
  annualBudget: number | null;
  /** Full vacation available (entitlement + carryOver) in `unit`; null when unknown/non-vacation. */
  available: number | null;
  /** Flexitime Ampel zone; null without configured thresholds. */
  zone?: OverviewFlexitimeBalance['zone'];
  /** HR thresholds of the flexitime balance type; null when not configured. */
  thresholds?: FlexitimeThresholds | null;
  /** Vacation split in `unit`: taken (past), approved (from today), requested (open requests). */
  split?: { taken: number; approved: number; requested: number };
  /** Whether `value` already includes the running day. */
  includesRunningDay?: boolean;
}

/**
 * Selectable absence type for the self-service capture (active `absence:*`
 * slot type definitions) — feeds the "Absenz" chips in "Deine Woche".
 */
export interface MyWeekAbsenceTypeOption {
  /** Type key without the `absence:` prefix (e.g. `vacation`). */
  key: string;
  /** Display label resolved to the request language. */
  label: string;
  icon: string | null;
  color: string | null;
  allowedPortions?: AbsencePortion[];
}

/** Employment default shift of a day; the times are UTC instants. */
export interface MyWeekDefaultShift {
  shiftUnitId: string;
  shiftName: string;
  startTime: string;
  endTime: string;
  teamId: string;
}

/** Day classification driving Soll: `holiday` and `free` have no target, `absence` is credited. */
export type MyWeekDayKind = 'work' | 'free' | 'holiday' | 'absence';

/** One day (Mon–Sun) of the "Your Week" view. */
export interface MyWeekDay {
  date: string;
  bookings: MyWeekBooking[];
  /** ALL actual-time bookings of the day (plan + stamps + manual; story 35-1). */
  entries: MyWeekEntry[];
  /** Read-only absence rows of the day (spec §3e-26) — never part of the sums. */
  absences: MyWeekAbsence[];
  plannedMinutes: number;
  actualMinutes: number;
  deviationMinutes: number;
  /** Aggregate status: needs_review > proposed > confirmed-ish; null = no bookings. */
  status: MyWeekReviewStatus | null;
  /** Present only for days without bookings that have a previous-week source. */
  suggestion?: MyWeekSuggestion | null;
  /** Per-day stats for the timeline split view; absent/null when not computed. */
  stats?: MyWeekDayStats | null;
  /** Employment default shifts of the day; they never count toward planned, actual or target minutes. */
  defaultShifts?: MyWeekDefaultShift[];
  /** Soll of the day in minutes: shift plan, else contract schedule, 0 on a holiday. */
  targetMinutes?: number;
  /** Minutes credited by absences of the day; part of the Ist, never of the worked time. */
  creditedAbsenceMinutes?: number;
  dayKind?: MyWeekDayKind;
  holiday?: { name: string };
  /** True when the day is locked (confirmed); month and day views render no edit action then. */
  locked?: boolean;
  /** Blocked windows (Sperrzeit) of the rule set; ISO-8601 UTC instants ending in Z. */
  blockedWindows?: Array<{
    start: string;
    end: string;
    windowKey?: string;
    severity?: BlockedWindowSeverity;
    legalReference?: string | null;
    source?: BlockedWindowSource;
  }>;
}

/**
 * Per-day statistics panel of the "Your Week" accordion body (split view:
 * booking rows left, day stats right). All values are provided by the caller —
 * the timeline component only renders them.
 */
export interface MyWeekDayStats {
  /** Total net worked minutes of the day. */
  workedMinutes: number;
  /** Target (Soll) net minutes of the day. */
  targetMinutes: number;
  /** Number of open findings with warn severity. */
  warnCount: number;
  /** Number of open findings with block severity (hard errors). */
  blockCount: number;
  /** Precomputed deviation-style text of the first finding; null when clean or key-driven. */
  issueLabel: string | null;
  /** i18n key of the first rule-violation finding (`verdicts.*.employee`); null when clean/deviation-only. */
  issueKey?: string | null;
  /** Interpolation params for `issueKey` (verdict `computedValues`, frozen at evaluation). */
  issueParams?: VerdictParams | null;
  /** Max severity of the day's open findings; null when clean/deviation-only. */
  issueSeverity?: ReviewQueueSeverity | null;
  /** All open findings ordered by severity descending; empty when clean. */
  issues?: MyWeekDayIssue[];
  /** Number of gaps between consecutive bookings of the day. */
  breakCount: number;
  /** Total minutes of those gaps. */
  breakMinutes: number;
  /** Day Saldo in minutes: Ist (worked + credited absence) − Soll; null for future days. */
  balanceMinutes: number | null;
}

/** One open finding of a day. */
export interface MyWeekDayIssue {
  key: string;
  params: VerdictParams | null;
  severity: ReviewQueueSeverity;
  /** Rule kind key, or `missing_clock_out` for a past open stamp. */
  kind?: string;
  /** Law reference of the rule; absent when the rule has none. */
  legalReference?: LegalReference;
  /** Active employee justification; absent while the finding is open. */
  justification?: FindingJustification;
  canJustify?: boolean;
  approvalState?: ApprovalState | null;
}

/** Body of `POST /time-tracking/my-week/suggestions/accept` (story 31-6). */
export const acceptSuggestionSchema = type({
  date: dayKeySchema,
});
export type AcceptSuggestionInput = typeof acceptSuggestionSchema.t;

// The shared base stays single-day; only self-service accepts a date range.
export const createMyWeekAbsenceBaseSchema = type({
  date: dayKeySchema,
  absenceType: 'string > 0',
})
  .and(absencePortionSchema)
  .narrow((payload, ctx) => portionCouplingError(payload) === null || ctx.mustBe('a portion with exactly its matching value fields'));

export const createMyWeekAbsenceSchema = createMyWeekAbsenceBaseSchema
  .and(type({ 'dateTo?': dayKeySchema }))
  .narrow(
    (payload, ctx) => payload.dateTo === undefined || payload.dateTo >= payload.date || ctx.mustBe('an end date on or after the start date')
  )
  .narrow(
    narrowError(({ date, dateTo }: { date: string; dateTo?: string }) => absenceRangeError({ dateFrom: date, dateTo: dateTo ?? date }))
  )
  .onUndeclaredKey('reject');
export type CreateMyWeekAbsenceInput = typeof createMyWeekAbsenceSchema.t;

/**
 * Self-service time capture. Times are UTC instants and future days are rejected.
 */
export const createMyWeekTimeSchema = type({
  date: dayKeySchema,
  startTime: utcInstantSchema,
  endTime: utcInstantSchema,
  'breakMinutes?': 'number.integer >= 0',
  'homeOffice?': 'boolean',
  'note?': 'string | null',
});
export type CreateMyWeekTimeInput = typeof createMyWeekTimeSchema.t;

export const updateMyWeekTimeSchema = type({
  startTime: utcInstantSchema,
  endTime: utcInstantSchema,
  'breakMinutes?': 'number.integer >= 0',
  'deviationReason?': "'overtime' | 'handover' | 'extended_shift' | null",
});
export type UpdateMyWeekTimeInput = typeof updateMyWeekTimeSchema.t;

export const convertMyWeekToAbsenceSchema = type({
  sourceKind: "'time'",
  sourceId: 'string.uuid',
  target: "'absence'",
  absenceType: 'string > 0',
});

export const convertMyWeekToTimeSchema = type({
  sourceKind: "'absence'",
  sourceId: 'string.uuid',
  target: "'time'",
  startTime: utcInstantSchema,
  endTime: utcInstantSchema,
  'breakMinutes?': 'number.integer >= 0',
});

// Discriminated union via `.or()` on sourceKind/target.
export const convertMyWeekEntrySchema = convertMyWeekToAbsenceSchema.or(convertMyWeekToTimeSchema);
export type ConvertMyWeekEntryInput = typeof convertMyWeekEntrySchema.t;

/**
 * Body of `POST /time-tracking/bookings/:id/draft-correction`: buffered save
 * of a shift-unit override that DOES NOT lock the day. Persists corrected
 * time / break / deviation reason on the active revision while keeping
 * `reviewStatus='proposed'`. Explicit lock happens only via the header flag
 * (`POST /my-week/days/:date/lock`).
 */
export const draftCorrectionSchema = type({
  'startTime?': utcInstantSchema,
  'endTime?': utcInstantSchema,
  'breakMinutes?': 'number.integer >= 0',
  'deviationReason?': "'overtime' | 'handover' | 'extended_shift'",
});
export type DraftCorrectionInput = typeof draftCorrectionSchema.t;

/** Result of `POST /my-week/bookings/:id/exclude` (disable shift unit) or `/include` (re-enable). */
export interface MyWeekBookingExclusionResult {
  bookingId: string;
  excluded: boolean;
}

export interface MyWeekResponse {
  week: string;
  /**
   * Server-side "today" (`YYYY-MM-DD`) in the ledger's server calendar —
   * the SAME key the week bulk confirm uses as its future cutoff. The UI must
   * derive bulk eligibility from this key, never from the browser clock
   * (browser TZ ≠ server TZ around midnight; spec §3a-3).
   */
  todayKey: string;
  /**
   * The caller's open clock stamp, if any (story 35-1). Week-INDEPENDENT: a
   * stamp opened before the requested week (boundary case) is still reported
   * so the running stamp never disappears from the HEUTE card.
   */
  openStamp: MyWeekOpenStamp | null;
  /**
   * Unconfirmed `proposed` plan days STRICTLY BEFORE `todayKey`, across ALL
   * weeks (spec §3e-27, F19c): the "N Tage unbestätigt" hint counter with the
   * earliest open date as the jump target. Today itself never counts — the
   * day is not over yet.
   */
  openDays: {
    count: number;
    firstDate: string | null;
  };
  days: MyWeekDay[];
  /**
   * Saldo widgets (admin-configured via `showInWeek` on the balance config),
   * ordered by the balance type's priority. Empty without a linked person.
   */
  balances: MyWeekBalance[];
  /**
   * Active absence types offered for self-service capture, ordered by
   * priority. Empty without a linked person.
   */
  absenceTypes: MyWeekAbsenceTypeOption[];
  /** Server clock; ISO-8601 UTC instant ending in Z. */
  serverNow?: string;
  /** Inclusive range of `days` in month and range mode; absent in week mode. */
  range?: { dateFrom: string; dateTo: string };
  /** First selectable month (`YYYY-MM`) of the month picker. */
  firstMonth?: string;
  totals: {
    plannedMinutes: number;
    actualMinutes: number;
    /** Σ of the day Soll over the elapsed days of `days`. */
    targetMinutes?: number;
    /** Σ of the credited absence minutes of `days`. */
    creditedAbsenceMinutes?: number;
    /** Σ of the day balances (Ist − Soll). */
    balanceMinutes?: number;
    /** Open findings of `days` without an active justification. */
    openFindings?: number;
    /** Findings of `days` with an active justification. */
    justifiedFindings?: number;
    /**
     * Σ per day of |actualMinutes − plannedMinutes| — fed from the SAME day
     * data as the day cards, so saved corrections and extra stamped time both
     * count (spec §3d-16, bug N1). NOT the sum of `day.deviationMinutes`
     * (that stays the plan-confirm deviation driving bulk eligibility).
     */
    deviationMinutes: number;
  };
}

export interface ConfirmWeekResultEntry {
  bookingId: string;
  date: string;
}

export interface ConfirmWeekSkippedEntry {
  bookingId: string;
  date: string;
  /** `future` = beyond the server-side "today" cutoff (PO feedback round 1, spec §3a-3). */
  reason: 'excluded' | 'deviation' | 'not_proposed' | 'future' | 'error';
}

export interface ConfirmWeekResponse {
  week: string;
  confirmed: ConfirmWeekResultEntry[];
  skipped: ConfirmWeekSkippedEntry[];
}

/**
 * Result of `POST /time-tracking/my-week/days/:date/lock` — locks every
 * `proposed` booking of the caller on that day (no correction, no deviation
 * reason). `confirmed` lists the booking ids that were transitioned; `skipped`
 * lists the ones that lost the optimistic guard race (parallel confirm).
 */
export interface MyWeekLockDayResult {
  confirmed: string[];
  skipped: string[];
}

/**
 * Result of `POST /time-tracking/my-week/days/:date/unlock` — resets every
 * `confirmed | auto_approved | needs_review` booking on that day back to
 * `proposed`. `skipped` lists booking ids in `rejected` or `approved`
 * (finalised reviewer decisions) plus any that lost the guard race.
 */
export interface MyWeekUnlockDayResult {
  unlocked: string[];
  skipped: string[];
}
